import {
  BUSINESS_PRICES,
  BUSINESS_SEAT_PACK_SIZE,
  businessSeatLimit,
  MAX_SEAT_PACKS,
} from '@/features/billing/lib/seatPacks';
import type { SubscriptionType } from '@/features/billing/types';
import { HStack, Icon, IconButton, Text, VStack } from '@/ui';

type Props = {
  subscriptionType: SubscriptionType;
  packs: number;
  onChange: (packs: number) => void;
};

// Seats for the business tier come in packs on top of the included seats; the
// picked count rides along to checkout as a second line item.
export const SeatPackSelector = ({
  subscriptionType,
  packs,
  onChange,
}: Props) => {
  const seats = businessSeatLimit(packs);
  const { base, pack } = BUSINESS_PRICES[subscriptionType];
  const total = base + pack * packs;
  const period = subscriptionType === 'yearly' ? 'year' : 'month';

  return (
    <VStack align="stretch" spacing={1} w="full">
      <HStack justify="space-between">
        <Text fontSize="sm" fontWeight="medium">
          {seats} accounts
        </Text>
        <HStack spacing={2}>
          <IconButton
            aria-label={`Remove ${BUSINESS_SEAT_PACK_SIZE} accounts`}
            icon={<Icon icon="Minus" />}
            isDisabled={packs <= 0}
            size="xs"
            variant="outline"
            onClick={() => onChange(Math.max(0, packs - 1))}
          />
          <IconButton
            aria-label={`Add ${BUSINESS_SEAT_PACK_SIZE} accounts`}
            icon={<Icon icon="Plus" />}
            isDisabled={packs >= MAX_SEAT_PACKS}
            size="xs"
            variant="outline"
            onClick={() => onChange(Math.min(MAX_SEAT_PACKS, packs + 1))}
          />
        </HStack>
      </HStack>
      <Text color="gray.600" fontSize="sm">
        {packs > 0
          ? `$${total}/${period} with ${packs} account ${packs === 1 ? 'pack' : 'packs'}`
          : `Add accounts in packs of ${BUSINESS_SEAT_PACK_SIZE}`}
      </Text>
    </VStack>
  );
};
